import { getStoredChatSession } from './useSession';

/**
 * Persists whether the AI chat drawer was open, scoped to the current browser tab.
 *
 * Uses sessionStorage on purpose: the conversation itself is kept in localStorage
 * (see useSession.ts) so it can be resumed from any tab, but whether the drawer is
 * open is a per-tab UI concern. A visitor who closes the drawer must not have it
 * reopen on the next page load (SO-3644), and a second tab should not inherit the
 * first tab's open drawer.
 */
const DRAWER_STATE_KEY = (orgId: string) => `ai_chat_drawer_state_${orgId}`;

/** Mirrors BubbleState in useAiChatWidget.ts; duplicated to avoid a circular import. */
const OPEN_AI_CHAT_STATE = 'ai-chat';

const hasSessionStorage = (): boolean => typeof sessionStorage !== 'undefined';

export function getStoredDrawerState(orgId: string): string | null {
  if (!hasSessionStorage() || !orgId) return null;
  try {
    return sessionStorage.getItem(DRAWER_STATE_KEY(orgId));
  } catch {
    return null;
  }
}

export function setStoredDrawerState(orgId: string, state: string): void {
  if (!hasSessionStorage() || !orgId) return;
  try {
    sessionStorage.setItem(DRAWER_STATE_KEY(orgId), state);
  } catch {
    // Storage may be unavailable (private mode, quota, disabled). The drawer still works for this page.
  }
}

export function clearStoredDrawerState(orgId: string): void {
  if (!hasSessionStorage() || !orgId) return;
  try {
    sessionStorage.removeItem(DRAWER_STATE_KEY(orgId));
  } catch {
    // Ignore storage errors
  }
}

/**
 * True only when this tab had the AI chat drawer open on the previous page AND there is a
 * resumable conversation to reconnect to. Having a stored conversation alone is not enough:
 * that is what made the drawer reopen after the visitor closed it.
 */
export function shouldRestoreChatOnLoad(orgId: string): boolean {
  if (getStoredDrawerState(orgId) !== OPEN_AI_CHAT_STATE) return false;
  const stored = getStoredChatSession(orgId);
  return Boolean(stored?.conversationId && stored?.userToken);
}
